/**
 * Posting Time Service
 *
 * Provides optimal posting time recommendations based on platform best practices.
 * Handles timezone detection and smart scheduling across a month.
 */

// ============================================================================
// OPTIMAL POSTING TIMES DATA
// Based on industry research and platform best practices
// ============================================================================

export const OPTIMAL_POSTING_TIMES = {
    instagram: {
        name: 'Instagram',
        weekday: ['09:00', '12:00', '17:00', '19:00'],  // Best engagement times
        weekend: ['10:00', '11:00', '14:00'],
        avoid: ['02:00', '03:00', '04:00', '05:00'],  // Low engagement
        peakDays: [1, 2, 3, 4],  // Mon-Thu (0=Sun)
        notes: 'Best during lunch breaks and evening commute'
    },
    facebook: {
        name: 'Facebook',
        weekday: ['09:00', '13:00', '16:00'],
        weekend: ['12:00', '13:00'],
        avoid: ['23:00', '00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00'],
        peakDays: [2, 3, 4],  // Tue-Thu
        notes: 'Engagement peaks mid-week during work breaks'
    },
    tiktok: {
        name: 'TikTok',
        weekday: ['07:00', '12:00', '19:00', '21:00'],  // High activity times
        weekend: ['09:00', '12:00', '19:00'],
        avoid: ['03:00', '04:00', '05:00'],
        peakDays: [2, 4, 5],  // Tue, Thu, Fri
        notes: 'Morning commute and evening entertainment hours'
    },
    linkedin: {
        name: 'LinkedIn',
        weekday: ['08:00', '10:00', '12:00', '17:00'],  // Business hours
        weekend: [],  // Avoid weekends for LinkedIn
        avoid: ['20:00', '21:00', '22:00', '23:00', '00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00'],
        peakDays: [2, 3, 4],  // Tue-Thu
        avoidDays: [0, 6],  // Sun, Sat
        notes: 'Professional hours during workweek only'
    },
    twitter: {
        name: 'Twitter/X',
        weekday: ['08:00', '12:00', '17:00', '21:00'],
        weekend: ['09:00', '12:00'],
        avoid: ['02:00', '03:00', '04:00', '05:00'],
        peakDays: [1, 2, 3, 4, 5],  // Mon-Fri
        notes: 'News consumption peaks in morning and evening'
    },
    google: {
        name: 'Google Ads',
        weekday: ['08:00', '10:00', '14:00', '18:00'],
        weekend: ['10:00', '14:00'],
        avoid: ['01:00', '02:00', '03:00', '04:00', '05:00'],
        peakDays: [1, 2, 3, 4, 5],  // Weekdays
        notes: 'Matches search behavior patterns'
    }
};

// Funnel stage priority weights for time slot allocation
const FUNNEL_PRIORITY = {
    BOFU: 1.0,  // Gets best times first
    MOFU: 0.8,
    TOFU: 0.6
};

// ============================================================================
// TIMEZONE UTILITIES
// ============================================================================

/**
 * Get user's local timezone
 */
export function getUserTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'America/New_York'; // Fallback
    }
}

/**
 * Format time for display in user's timezone
 */
export function formatTimeForTimezone(date, timezone = getUserTimezone()) {
    return new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone
    }).format(date);
}

/**
 * Format date for display
 */
export function formatDateForDisplay(date, timezone = getUserTimezone()) {
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: timezone
    }).format(date);
}

// ============================================================================
// OPTIMAL TIME FUNCTIONS
// ============================================================================

/**
 * Get optimal times for a specific date and platforms
 */
export function getOptimalTimesForDate(date, platforms = ['instagram']) {
    const dayOfWeek = date.getDay(); // 0 = Sunday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    const allTimes = new Set();
    const avoidTimes = new Set();

    platforms.forEach(platform => {
        const config = OPTIMAL_POSTING_TIMES[platform];
        if (!config) return;

        // Check if this day should be avoided for this platform
        if (config.avoidDays?.includes(dayOfWeek)) {
            return;
        }

        // Add recommended times
        const times = isWeekend ? config.weekend : config.weekday;
        times.forEach(time => allTimes.add(time));

        // Add avoid times
        config.avoid?.forEach(time => avoidTimes.add(time));
    });

    // Remove avoided times from recommended
    avoidTimes.forEach(time => allTimes.delete(time));

    return {
        recommended: Array.from(allTimes).sort(),
        avoid: Array.from(avoidTimes).sort(),
        isWeekend,
        dayOfWeek
    };
}

/**
 * Check if a time slot is optimal for given platforms
 */
export function isOptimalTime(time, platforms = ['instagram']) {
    for (const platform of platforms) {
        const config = OPTIMAL_POSTING_TIMES[platform];
        if (!config) continue;

        const allOptimalTimes = [...config.weekday, ...config.weekend];
        if (allOptimalTimes.includes(time)) {
            return true;
        }
    }
    return false;
}

/**
 * Check if a time should be avoided
 */
export function isAvoidTime(time, platforms = ['instagram']) {
    for (const platform of platforms) {
        const config = OPTIMAL_POSTING_TIMES[platform];
        if (!config) continue;

        if (config.avoid?.includes(time)) {
            return true;
        }
    }
    return false;
}

/**
 * Get time slot quality rating
 * Returns: 'optimal', 'good', 'avoid'
 */
export function getTimeSlotQuality(time, date, platforms = ['instagram']) {
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

    // Check if any platform explicitly avoids this time
    for (const platform of platforms) {
        const config = OPTIMAL_POSTING_TIMES[platform];
        if (!config) continue;

        if (config.avoid?.includes(time)) {
            return 'avoid';
        }

        if (config.avoidDays?.includes(dayOfWeek)) {
            return 'avoid';
        }
    }

    // Check if this is an optimal time for any platform
    for (const platform of platforms) {
        const config = OPTIMAL_POSTING_TIMES[platform];
        if (!config) continue;

        const optimalTimes = isWeekend ? config.weekend : config.weekday;
        if (optimalTimes.includes(time)) {
            return 'optimal';
        }
    }

    return 'good';
}

// ============================================================================
// SMART SCHEDULING FUNCTIONS
// ============================================================================

/**
 * Distribute briefs across a month with optimal posting times
 *
 * @param {Array} briefs - Approved briefs with generated creatives
 * @param {number} month - 0-indexed month
 * @param {number} year - Year
 * @param {Array} platforms - Target platforms
 * @param {string} timezone - User's timezone
 * @returns {Array} - Scheduled posts with dates and times
 */
export function distributePostsAcrossMonth(briefs, month, year, platforms = ['instagram'], timezone = getUserTimezone(), options = {}) {
    if (!briefs || briefs.length === 0) {
        return [];
    }

    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    return distributePostsInDateRange(briefs, startDate, endDate, platforms, timezone, options);
}

/**
 * Get eligible dates in a range, optionally excluding weekends
 */
function getEligibleDates(startDate, endDate, includeWeekends = true) {
    const dates = [];
    const current = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();

    while (current <= end) {
        const dayOfWeek = current.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

        if (includeWeekends || !isWeekend) {
            // Only include dates that aren't fully in the past
            const endOfDay = new Date(current);
            endOfDay.setHours(23, 59, 59, 999);
            if (endOfDay >= now) {
                dates.push(new Date(current));
            }
        }
        current.setDate(current.getDate() + 1);
    }
    return dates;
}

/**
 * Distribute briefs across a date range with optimal posting times
 *
 * @param {Array} briefs - Approved briefs with generated creatives
 * @param {Date} startDate - Start date of range
 * @param {Date} endDate - End date of range
 * @param {Array} platforms - Target platforms
 * @param {string} timezone - User's timezone
 * @param {Object} options - Scheduling options
 * @param {string} options.mode - 'even' (spread evenly) or 'sequential' (one per day from start)
 * @param {boolean} options.includeWeekends - Whether to include weekends (default true)
 * @returns {Array} - Scheduled posts with dates and times
 */
export function distributePostsInDateRange(briefs, startDate, endDate, platforms = ['instagram'], timezone = getUserTimezone(), options = {}) {
    if (!briefs || briefs.length === 0) {
        return [];
    }

    const { mode = 'even', includeWeekends = true } = options;

    const scheduledPosts = [];
    const usedSlots = new Map(); // Map of date+time -> count

    // Sort briefs by funnel priority (BOFU first to get best times)
    const sortedBriefs = [...briefs].sort((a, b) => {
        const priorityA = FUNNEL_PRIORITY[a.funnelStage] || 0.5;
        const priorityB = FUNNEL_PRIORITY[b.funnelStage] || 0.5;
        return priorityB - priorityA;
    });

    // Get eligible dates (respecting weekend preference)
    const eligibleDates = getEligibleDates(startDate, endDate, includeWeekends);

    if (eligibleDates.length === 0) {
        return [];
    }

    // Calculate posts per day
    const postsPerDay = mode === 'sequential'
        ? 1  // Sequential: 1 post per day, overflow if more posts than days
        : Math.ceil(briefs.length / eligibleDates.length);

    // Track posts per day
    const postsOnDay = new Map();

    sortedBriefs.forEach((brief, index) => {
        const briefPlatforms = brief.platforms?.length > 0 ? brief.platforms : platforms;

        let slot;
        if (mode === 'sequential') {
            slot = findSequentialSlot(
                eligibleDates,
                briefPlatforms,
                usedSlots,
                postsOnDay,
                index
            );
        } else {
            slot = findEvenlyDistributedSlot(
                eligibleDates,
                briefPlatforms,
                usedSlots,
                postsOnDay,
                postsPerDay,
                index,
                sortedBriefs.length
            );
        }

        if (slot) {
            // Mark slot as used
            const slotKey = `${slot.date.toISOString().split('T')[0]}_${slot.time}`;
            usedSlots.set(slotKey, (usedSlots.get(slotKey) || 0) + 1);

            // Increment posts on this day
            const dayKey = slot.date.toISOString().split('T')[0];
            postsOnDay.set(dayKey, (postsOnDay.get(dayKey) || 0) + 1);

            scheduledPosts.push({
                briefId: brief.id,
                brief,
                date: slot.date,
                time: slot.time,
                dateTime: slot.dateTime,
                platforms: briefPlatforms,
                quality: slot.quality,
                suggestedBy: 'auto'
            });
        }
    });

    // Align A/B test pairs to the same date/time
    // Group by testId, then set variant B to match variant A's schedule
    const testGroups = new Map();
    for (const post of scheduledPosts) {
        const testId = post.brief?.abTest?.testId;
        if (testId) {
            if (!testGroups.has(testId)) testGroups.set(testId, []);
            testGroups.get(testId).push(post);
        }
    }
    for (const posts of testGroups.values()) {
        // Find variant A (the anchor)
        const variantA = posts.find(p => p.brief?.abTest?.isVariantA);
        if (variantA) {
            for (const post of posts) {
                if (post !== variantA) {
                    post.date = variantA.date;
                    post.time = variantA.time;
                    post.dateTime = variantA.dateTime;
                    post.quality = variantA.quality;
                }
            }
        }
    }

    // Sort by date/time
    scheduledPosts.sort((a, b) => a.dateTime - b.dateTime);

    return scheduledPosts;
}

/**
 * Find a slot for sequential distribution (one post per day, in order)
 */
function findSequentialSlot(eligibleDates, platforms, usedSlots, postsOnDay, briefIndex) {
    // Assign posts to dates sequentially, wrapping around if more posts than dates
    const dateIndex = briefIndex % eligibleDates.length;
    const targetDate = eligibleDates[dateIndex];

    // If wrapping, allow multiple posts per day
    const wrap = Math.floor(briefIndex / eligibleDates.length);
    const maxPerDay = wrap + 1;

    const dayKey = targetDate.toISOString().split('T')[0];
    const currentPostsOnDay = postsOnDay.get(dayKey) || 0;

    if (currentPostsOnDay >= maxPerDay) {
        // Shouldn't happen with wrap logic, but fall through to find any slot
        return findAnyAvailableSlot(eligibleDates, platforms, usedSlots);
    }

    // Get optimal times for this day
    const { recommended } = getOptimalTimesForDate(targetDate, platforms);

    // Try each optimal time
    for (const time of recommended) {
        const slotKey = `${dayKey}_${time}`;
        const slotUsage = usedSlots.get(slotKey) || 0;

        if (slotUsage < 2) {
            const [hours, minutes] = time.split(':').map(Number);
            const dateTime = new Date(targetDate);
            dateTime.setHours(hours, minutes, 0, 0);

            if (dateTime < new Date()) continue;

            return {
                date: new Date(targetDate),
                time,
                dateTime,
                quality: getTimeSlotQuality(time, targetDate, platforms)
            };
        }
    }

    // Fallback to any time between 8am-8pm
    for (let hour = 8; hour <= 20; hour++) {
        const time = `${String(hour).padStart(2, '0')}:00`;
        const slotKey = `${dayKey}_${time}`;
        const slotUsage = usedSlots.get(slotKey) || 0;

        if (slotUsage < 2) {
            const dateTime = new Date(targetDate);
            dateTime.setHours(hour, 0, 0, 0);

            if (dateTime < new Date()) continue;

            return {
                date: new Date(targetDate),
                time,
                dateTime,
                quality: getTimeSlotQuality(time, targetDate, platforms)
            };
        }
    }

    return findAnyAvailableSlot(eligibleDates, platforms, usedSlots);
}

/**
 * Find a slot for even distribution across eligible dates
 */
function findEvenlyDistributedSlot(eligibleDates, platforms, usedSlots, postsOnDay, maxPerDay, briefIndex, totalBriefs) {
    // Calculate target date index based on even distribution
    const targetDateIndex = Math.floor((briefIndex / totalBriefs) * eligibleDates.length);

    // Search window (prefer near target, expand if needed)
    for (let offset = 0; offset <= eligibleDates.length; offset++) {
        const indicesToTry = offset === 0
            ? [targetDateIndex]
            : [targetDateIndex + offset, targetDateIndex - offset].filter(i => i >= 0 && i < eligibleDates.length);

        for (const dateIndex of indicesToTry) {
            const date = eligibleDates[dateIndex];
            const dayKey = date.toISOString().split('T')[0];

            // Check if this day is full
            const currentPostsOnDay = postsOnDay.get(dayKey) || 0;
            if (currentPostsOnDay >= maxPerDay) continue;

            // Get optimal times for this day
            const { recommended } = getOptimalTimesForDate(date, platforms);

            // Try each optimal time
            for (const time of recommended) {
                const slotKey = `${dayKey}_${time}`;
                const slotUsage = usedSlots.get(slotKey) || 0;

                if (slotUsage < 2) {
                    const [hours, minutes] = time.split(':').map(Number);
                    const dateTime = new Date(date);
                    dateTime.setHours(hours, minutes, 0, 0);

                    if (dateTime < new Date()) continue;

                    return {
                        date: new Date(date),
                        time,
                        dateTime,
                        quality: getTimeSlotQuality(time, date, platforms)
                    };
                }
            }

            // Fallback to any time between 8am-8pm
            for (let hour = 8; hour <= 20; hour++) {
                const time = `${String(hour).padStart(2, '0')}:00`;
                const slotKey = `${dayKey}_${time}`;
                const slotUsage = usedSlots.get(slotKey) || 0;

                if (slotUsage < 2) {
                    const dateTime = new Date(date);
                    dateTime.setHours(hour, 0, 0, 0);

                    if (dateTime < new Date()) continue;

                    return {
                        date: new Date(date),
                        time,
                        dateTime,
                        quality: getTimeSlotQuality(time, date, platforms)
                    };
                }
            }
        }
    }

    return findAnyAvailableSlot(eligibleDates, platforms, usedSlots);
}

/**
 * Fallback: find any available slot across eligible dates
 */
function findAnyAvailableSlot(eligibleDates, platforms, usedSlots) {
    for (const date of eligibleDates) {
        const time = '12:00';
        const dateTime = new Date(date);
        dateTime.setHours(12, 0, 0, 0);

        if (dateTime > new Date()) {
            return { date: new Date(date), time, dateTime, quality: 'good' };
        }
    }
    return null;
}

/**
 * Suggest the next available slot given existing scheduled posts
 */
export function suggestNextAvailableSlot(existingPosts, platforms = ['instagram'], timezone = getUserTimezone()) {
    const now = new Date();
    const usedSlots = new Map();

    // Build map of used slots from existing posts
    existingPosts.forEach(post => {
        const dateStr = post.date instanceof Date
            ? post.date.toISOString().split('T')[0]
            : post.date.split('T')[0];
        const key = `${dateStr}_${post.time}`;
        usedSlots.set(key, (usedSlots.get(key) || 0) + 1);
    });

    // Try to find a slot in the next 7 days
    for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const date = new Date(now);
        date.setDate(date.getDate() + dayOffset);

        const { recommended } = getOptimalTimesForDate(date, platforms);

        for (const time of recommended) {
            const [hours, minutes] = time.split(':').map(Number);
            const slotDateTime = new Date(date);
            slotDateTime.setHours(hours, minutes, 0, 0);

            // Skip past times
            if (slotDateTime <= now) {
                continue;
            }

            const dayKey = date.toISOString().split('T')[0];
            const slotKey = `${dayKey}_${time}`;

            if (!usedSlots.has(slotKey)) {
                return {
                    date,
                    time,
                    dateTime: slotDateTime,
                    quality: 'optimal'
                };
            }
        }
    }

    // Fallback to tomorrow at noon
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(12, 0, 0, 0);

    return {
        date: tomorrow,
        time: '12:00',
        dateTime: tomorrow,
        quality: 'good'
    };
}

/**
 * Get platform-specific posting recommendations
 */
export function getPlatformRecommendations(platform) {
    const config = OPTIMAL_POSTING_TIMES[platform];
    if (!config) {
        return null;
    }

    return {
        name: config.name,
        bestTimes: config.weekday,
        weekendTimes: config.weekend,
        peakDays: config.peakDays?.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]),
        avoidDays: config.avoidDays?.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]),
        notes: config.notes
    };
}

export default {
    OPTIMAL_POSTING_TIMES,
    getUserTimezone,
    formatTimeForTimezone,
    formatDateForDisplay,
    getOptimalTimesForDate,
    isOptimalTime,
    isAvoidTime,
    getTimeSlotQuality,
    distributePostsAcrossMonth,
    distributePostsInDateRange,
    suggestNextAvailableSlot,
    getPlatformRecommendations
};
